import React, { useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  Collapse,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * InformationStepsCard Component
 * A clean, minimal, Steve Jobs-style component to display instructional steps
 *
 * Supports:
 * - Plain strings
 * - Markdown formatting (bold, italic, links, code, etc.)
 * - Full JSX elements for complex content
 *
 * @param {string|JSX.Element} heading - The main heading (supports Markdown)
 * @param {string|JSX.Element} subtitle - Optional subtitle (supports Markdown)
 * @param {Array<string|JSX.Element>} steps - Array of steps (supports Markdown)
 * @param {string|JSX.Element} footerText - Optional footer (supports Markdown)
 * @param {boolean} collapsible - Whether the content can be collapsed (default: true)
 * @param {boolean} defaultExpanded - Whether expanded by default (default: true)
 */
const InformationStepsCard = ({
  heading,
  subtitle,
  steps = [],
  footerText,
  collapsible = true,
  defaultExpanded = true,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleToggle = () => {
    if (collapsible) {
      setExpanded(!expanded);
    }
  };

  // Shared markdown components configuration
  const markdownComponents = {
    p: ({ children, ...props }) => (
      <Typography
        variant="body2"
        component="span"
        sx={{ display: "inline" }}
        {...props}
      >
        {children}
      </Typography>
    ),
    strong: ({ children }) => (
      <Box
        component="strong"
        sx={{
          fontWeight: 600,
          color: theme.customColors.text.primary,
          letterSpacing: "-0.01em",
        }}
      >
        {children}
      </Box>
    ),
    em: ({ children }) => (
      <Box
        component="em"
        sx={{
          fontStyle: "italic",
          color: theme.customColors.text.secondary,
        }}
      >
        {children}
      </Box>
    ),
    a: ({ href, children }) => {
      return (
        <MuiLink
          href={href}
          underline="hover"
          color="primary"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontSize: "inherit",
            fontWeight: 500,
            letterSpacing: "-0.005em",
          }}
        >
          {children}
        </MuiLink>
      );
    },
    code: ({ children }) => (
      <Box
        component="code"
        sx={{
          px: 0.75,
          py: 0.25,
          backgroundColor: theme.customColors.background.tableRow,
          borderRadius: "5px",
          fontSize: "0.92em",
          fontFamily: "monospace",
          color: theme.customColors.text.primary,
        }}
      >
        {children}
      </Box>
    ),
  };

  // Render markdown or JSX content
  const renderContent = (content, wrapperSx = {}) => {
    if (typeof content === "string") {
      return (
        <Box sx={{ ...wrapperSx, "& p": { margin: 0 } }}>
          <ReactMarkdown
            components={markdownComponents}
            remarkPlugins={[remarkGfm]}
          >
            {content}
          </ReactMarkdown>
        </Box>
      );
    }
    return <Box sx={{ ...wrapperSx, "& > *": wrapperSx }}>{content}</Box>;
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.customColors.background.info,
        border: `1px solid ${theme.customColors.border.subtle}`,
        borderRadius: "10px",
        mb: 3,
        overflow: "hidden",
        transition: "all 0.2s ease-in-out",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        "&:hover": {
          boxShadow: collapsible
            ? "0 2px 8px rgba(0, 0, 0, 0.06)"
            : "0 1px 3px rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          px: 2.5,
          py: 2,
          cursor: collapsible ? "pointer" : "default",
          userSelect: "none",
        }}
        onClick={handleToggle}
      >
        {/* Info Icon */}
        <InfoOutlinedIcon
          sx={{
            fontSize: 20,
            color: theme.customColors.icons.primary,
            mt: 0.1,
            flexShrink: 0,
          }}
        />

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Heading */}
          {renderContent(heading, {
            mb: subtitle ? 0.75 : 0,
            fontWeight: 600,
            color: theme.customColors.text.strong,
            lineHeight: 1.5,
            letterSpacing: "-0.01em",
          })}

          {/* Subtitle */}
          {subtitle &&
            renderContent(subtitle, {
              mb: expanded ? 1.5 : 0,
              color: theme.customColors.text.secondary,
              lineHeight: 1.6,
              letterSpacing: "-0.005em",
            })}

          {/* Collapsible Content */}
          <Collapse in={expanded}>
            {/* Steps */}
            {steps.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {steps.map((step, index) => {
                  // Extract step content - handle both string and object formats
                  const stepContent =
                    typeof step === "string" ? step : step.description || "";

                  // Skip empty strings (used for spacing)
                  if (stepContent.trim() === "") {
                    return <Box key={index} sx={{ height: 4 }} />;
                  }

                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        gap: 1.25,
                        alignItems: "flex-start",
                      }}
                    >
                      {/* Bullet Point */}
                      <Box
                        sx={{
                          width: 4,
                          height: 4,
                          borderRadius: "50%",
                          backgroundColor: theme.customColors.icons.primary,
                          mt: 1.25,
                          flexShrink: 0,
                          opacity: 0.6,
                        }}
                      />
                      {/* Step Text */}
                      {renderContent(stepContent, {
                        flex: 1,
                        lineHeight: 1.35,
                        color: theme.customColors.text.secondary,
                        letterSpacing: "-0.005em",
                      })}
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Footer */}
            {footerText &&
              renderContent(footerText, {
                mt: 1.75,
                pt: 1.75,
                borderTop: `1px solid ${theme.customColors.border.subtle}`,
                fontSize: "13px",
                lineHeight: 1.6,
                color: theme.customColors.text.muted,
                letterSpacing: "-0.005em",
              })}
          </Collapse>
        </Box>

        {/* Collapse/Expand Icon */}
        {collapsible && (
          <Box sx={{ flexShrink: 0, mt: 0.1 }}>
            {expanded ? (
              <ExpandLessIcon
                sx={{
                  fontSize: 18,
                  color: theme.customColors.text.iconMuted,
                  transition: "transform 0.2s ease",
                }}
              />
            ) : (
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  color: theme.customColors.text.iconMuted,
                  transition: "transform 0.2s ease",
                }}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InformationStepsCard;
